import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { useMemo } from 'react';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import SahamAccordion from 'components/SahamAccordion';
import { Config, Listings, SagisConfig } from 'containers/AppContainer/typings';
import { REGULATIONS } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import { StyledDiv } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/styled-components';
import {
  beneficiaryRecipientCases,
  getLinkWithRecepient,
  getChosenCity,
  getJudiciarySettlementChannel,
  getPaymentNature,
  setPaymentChannelByBeneficiaryRecipient,
  getLinkWithRecepientForMaterialRisk,
  paymentMethod,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import { RECIPIENT } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import {
  getCities,
  getCountriesWithCities,
} from 'containers/RecapManagerPage/utils';
import { ClaimRecapType } from 'typings/claimTypes';

export interface AdditionalWarrentiesInformationProps {
  intl: IntlShape;
  sagisClaim: any;
  claimRecap: ClaimRecapType;
  setRiskRecap: Function;
  riskRecap: any;
  listings: Listings;
  risk: any;
  isStepValidated: any;
  setMessageError: any;
  messageError: any;
  activeStep: any;
}

export const AdditionalWarrentiesInformation: React.FC<AdditionalWarrentiesInformationProps> = ({
  sagisClaim,
  claimRecap,
  listings,
  risk,
  isStepValidated,
  messageError,
  activeStep,
  ...props
}): JSX.Element => {
  const handleChange = (risk: any) => (
    event: React.ChangeEvent<{ name?: string | undefined; value: any }>,
    valueField?: any,
  ) => {
    const risks = [...props.riskRecap];
    const regulationInfo = get(
      risks[risks.indexOf(risk)],
      'regulationInfo',
      {},
    );
    if (event.target.name) {
      switch (event.target.name) {
        case REGULATIONS.BENEFICIARY_RECIPIENT:
          regulationInfo[event.target.name] = event.target.value;
          setPaymentChannelByBeneficiaryRecipient(
            judiciaryChannel,
            regulationInfo,
            event.target.value,
          );
          beneficiaryRecipientCases(
            event.target.value,
            regulationInfo,
            get(listings, 'PaymentMethod', []),
            get(listings, 'appConfig', []),
            get(listings, 'EncryptDocumentCity', []),
          );
          break;
        default:
          regulationInfo[event.target.name] = event.target.value;
          break;
      }
    }

    if (get(valueField, 'ItemType') === 'Town')
      regulationInfo[REGULATIONS.TOWN_CODE] = get(valueField, 'ItemKey', '');
    props.setRiskRecap(risks);
  };

  const handleChangeExpanded = (event: React.ChangeEvent<{}>, item: any) => {
    const types = [...props.riskRecap] as any;
    const currentType =
      types.find((type: any) => type.riskName === item.riskName) || {};

    if (!isEmpty(currentType)) {
      const index = types.indexOf(currentType);

      types[index].regulationInfo.expand = !get(
        item,
        'regulationInfo.expand',
        false,
      );
      return props.setRiskRecap(types);
    }
  };
  const linkWithRecipient = getLinkWithRecepient(
    get(listings, 'LinesOfRecipt', []),
  );
  const judiciaryChannel = useMemo(
    () =>
      getJudiciarySettlementChannel(
        get(listings, 'PaymentChannel', []),
        get(listings, 'PaymentNature', []),
        risk,
        props.riskRecap,
        props.setRiskRecap,
      ),
    [listings],
  );
  const countries: SagisConfig[] = getCountriesWithCities(
    get(listings, 'country', []),
    get(listings, 'villes', []),
  );
  const filterOptions = createFilterOptions({
    matchFrom: 'start',
    stringify: (option: SagisConfig) => option?.ItemDescription,
  });
  return (
    <StyledDiv key={risk.regulationId}>
      {!isEmpty(get(risk, 'data', [])) && (
        <SahamAccordion
          key={risk.regulationId}
          title={`${translate(messages.riskInfo, props)} - ${risk.riskName}`}
          expanded={get(risk, 'regulationInfo.expand', false)}
          onChange={_ => handleChangeExpanded(_, risk)}
        >
          <Grid container spacing={4} className="regulation-info">
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>
                  {translate(messages.regulationPhase, props)}
                </InputLabel>

                <Select
                  onChange={handleChange(risk)}
                  data-testId={REGULATIONS.PAYMENT_NATURE}
                  name={REGULATIONS.PAYMENT_NATURE}
                  id={REGULATIONS.PAYMENT_NATURE}
                  value={get(risk, 'regulationInfo.PaymentNatureCode', '')}
                  error={
                    get(
                      messageError[risk.regulationId],
                      [REGULATIONS.PAYMENT_NATURE],
                      '',
                    ) && isStepValidated[activeStep]
                  }
                >
                  {[
                    getPaymentNature(get(listings, 'PaymentNature', [])) as any,
                  ].map((el: SagisConfig) => (
                    <MenuItem
                      data-testId={el.ItemKey}
                      id={el.ItemKey}
                      key={el.ItemKey}
                      value={el.ItemKey}
                    >
                      {el.ItemDescription}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>
                  {translate(messages.paymentMethod, props)}
                </InputLabel>

                <Select
                  onChange={handleChange(risk)}
                  data-testid={REGULATIONS.PAYMENT_METHOD}
                  name={REGULATIONS.PAYMENT_METHOD}
                  id={REGULATIONS.PAYMENT_METHOD}
                  value={get(
                    risk.regulationInfo,
                    REGULATIONS.PAYMENT_METHOD,
                    '',
                  )}
                  error={
                    get(
                      messageError[risk.regulationId],
                      [REGULATIONS.PAYMENT_METHOD],
                      '',
                    ) && isStepValidated[activeStep]
                  }
                >
                  {paymentMethod(get(listings, 'PaymentMethod', [])).map(
                    (el: SagisConfig) => (
                      <MenuItem
                        data-testid={el.ItemKey}
                        id={el.ItemKey}
                        key={el.ItemKey}
                        value={el.ItemKey}
                      >
                        {el.ItemDescription}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="idd-label-id">
                  {translate(messages.recipient, props)}
                </InputLabel>
                <Select
                  onChange={handleChange(risk)}
                  data-testId={REGULATIONS.BENEFICIARY_RECIPIENT}
                  name={REGULATIONS.BENEFICIARY_RECIPIENT}
                  id={REGULATIONS.BENEFICIARY_RECIPIENT}
                  value={get(risk, 'regulationInfo.beneficiaryRecipient', '')}
                  error={
                    get(
                      messageError[risk.regulationId],
                      [REGULATIONS.BENEFICIARY_RECIPIENT],
                      '',
                    ) && isStepValidated[activeStep]
                  }
                >
                  {get(listings, 'regulationRecipient', []).map(
                    (el: Config) => (
                      <MenuItem
                        data-testId={el.title}
                        key={el.id}
                        value={el.title}
                      >
                        {el.title}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </Grid>
            {get(risk, 'regulationInfo.beneficiaryRecipient', '') ===
              RECIPIENT.OTHERS && (
              <Grid item xs={4} className="other-item">
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    onChange={handleChange(risk)}
                    data-testId={REGULATIONS.OTHER_RECIPIENT}
                    name={REGULATIONS.OTHER_RECIPIENT}
                    value={get(risk, 'regulationInfo.otherRecipient', '')}
                  />
                </FormControl>
              </Grid>
            )}
            {get(risk, 'regulationInfo.beneficiaryRecipient', '') ===
              RECIPIENT.OTHERS && (
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel>
                    {translate(messages.paymentChannel, props)}
                  </InputLabel>

                  <Select
                    onChange={handleChange(risk)}
                    data-testid={REGULATIONS.PAYMENT_CHANNEL}
                    name={REGULATIONS.PAYMENT_CHANNEL}
                    id={REGULATIONS.PAYMENT_CHANNEL}
                    value={get(
                      risk.regulationInfo,
                      REGULATIONS.PAYMENT_CHANNEL,
                      '',
                    )}
                    error={
                      get(
                        messageError[risk.regulationId],
                        [REGULATIONS.PAYMENT_CHANNEL],
                        '',
                      ) && isStepValidated[activeStep]
                    }
                  >
                    {judiciaryChannel.map((el: SagisConfig) => (
                      <MenuItem
                        data-testid={el.ItemKey}
                        id={el.ItemKey}
                        key={el.ItemKey}
                        value={el.ItemKey}
                      >
                        {el.ItemDescription}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {get(risk, 'regulationInfo.beneficiaryRecipient', '') ===
              RECIPIENT.CDRA && (
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel id="idd-label-id">
                    {translate(messages.realBeneficiary, props)}
                  </InputLabel>
                  <Select
                    onChange={handleChange(risk)}
                    data-testId={REGULATIONS.REAL_BENEFICIARY}
                    name={REGULATIONS.REAL_BENEFICIARY}
                    id={REGULATIONS.REAL_BENEFICIARY}
                    value={get(risk, 'regulationInfo.realBeneficiary', '')}
                    error={
                      get(
                        messageError[risk.regulationId],
                        [REGULATIONS.REAL_BENEFICIARY],
                        '',
                      ) && isStepValidated[activeStep]
                    }
                  >
                    {get(listings, 'bar', []).map((el: Config) => (
                      <MenuItem
                        data-testId={el.title}
                        key={el.id}
                        value={el.title}
                      >
                        {el.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {get(risk, 'regulationInfo.beneficiaryRecipient', '') ===
              RECIPIENT.BNEJ && (
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    label={translate(messages.realBeneficiary, props)}
                    onChange={handleChange(risk)}
                    data-testId={REGULATIONS.REAL_BENEFICIARY}
                    name={REGULATIONS.REAL_BENEFICIARY}
                    value={get(risk, 'regulationInfo.realBeneficiary', '')}
                    disabled={true}
                    error={
                      get(
                        messageError[risk.regulationId],
                        [REGULATIONS.REAL_BENEFICIARY],
                        '',
                      ) && isStepValidated[activeStep]
                    }
                  />
                </FormControl>
              </Grid>
            )}
            {get(risk, 'regulationInfo.beneficiaryRecipient', '') ===
              RECIPIENT.OTHERS && (
              <>
                <Grid item xs={4}>
                  <FormControl fullWidth>
                    <InputLabel id="idd-label-id">
                      {translate(messages.realBeneficiary, props)}
                    </InputLabel>
                    <Select
                      onChange={handleChange(risk)}
                      data-testId={REGULATIONS.REAL_BENEFICIARY}
                      name={REGULATIONS.REAL_BENEFICIARY}
                      id={REGULATIONS.REAL_BENEFICIARY}
                      value={get(risk, 'regulationInfo.realBeneficiary', '')}
                      error={
                        get(
                          messageError[risk.regulationId],
                          [REGULATIONS.REAL_BENEFICIARY],
                          '',
                        ) && isStepValidated[activeStep]
                      }
                    >
                      {linkWithRecipient.map((el: SagisConfig) => (
                        <MenuItem
                          data-testId={el.ItemDescription}
                          key={el.ItemKey}
                          value={el.ItemDescription}
                        >
                          {el.ItemDescription}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {get(risk, 'regulationInfo.realBeneficiary', '') ===
                  RECIPIENT.OTHERS && (
                  <Grid item xs={4} className="other-item">
                    <TextField
                      fullWidth
                      onChange={handleChange(risk)}
                      data-testId={REGULATIONS.OTHER_REAL_BENEFICIARY}
                      name={REGULATIONS.OTHER_REAL_BENEFICIARY}
                      value={get(
                        risk,
                        'regulationInfo.otherRealBeneficiary',
                        '',
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label={translate(messages.additionalAddress, props)}
                    onChange={handleChange(risk)}
                    data-testId={REGULATIONS.ADDITIONAL_ADDRESS}
                    name={REGULATIONS.ADDITIONAL_ADDRESS}
                    value={get(risk, 'regulationInfo.complement', '')}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel id="idd-label-id">
                  {translate(messages.linkToBeneficiary, props)}
                </InputLabel>
                <Select
                  onChange={handleChange(risk)}
                  data-testid={REGULATIONS.LINK_TO_BENEFICIARY}
                  name={REGULATIONS.LINK_TO_BENEFICIARY}
                  id={REGULATIONS.LINK_TO_BENEFICIARY}
                  value={get(
                    risk.regulationInfo,
                    [REGULATIONS.LINK_TO_BENEFICIARY],
                    '',
                  )}
                  error={
                    get(
                      messageError[risk.regulationId],
                      [REGULATIONS.LINK_TO_BENEFICIARY],
                      '',
                    ) && isStepValidated[activeStep]
                  }
                >
                  {getLinkWithRecepientForMaterialRisk(
                    get(listings, 'LinesOfRecipt', []),
                  ).map((el: SagisConfig) => (
                    <MenuItem
                      data-testid={el.ItemDescription}
                      key={el.ItemDescription}
                      value={el.ItemDescription}
                    >
                      {el.ItemDescription}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {get(risk, 'regulationInfo.beneficiaryRecipient', '') !==
              RECIPIENT.OTHERS && (
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    label={translate(messages.additionalAddress, props)}
                    onChange={handleChange(risk)}
                    name={REGULATIONS.ADDITIONAL_ADDRESS}
                    value={get(risk, 'regulationInfo.realBeneficiary', '')}
                    disabled={true}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid item xs={4}>
              <FormControl fullWidth>
                <TextField
                  fullWidth
                  label={translate(messages.beneficiaryReference, props)}
                  onChange={handleChange(risk)}
                  data-testId={REGULATIONS.BENEFICIARY_REFERENCE}
                  name={REGULATIONS.BENEFICIARY_REFERENCE}
                  value={get(risk, 'regulationInfo.beneficiaryReference', '')}
                />
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>
                  {translate({ ...messages.country }, props)}
                </InputLabel>
                <Select
                  onChange={handleChange(risk)}
                  name={REGULATIONS.COUNTRY_CODE}
                  value={get(risk, 'regulationInfo.countryCode', '')}
                  fullWidth
                  error={
                    get(
                      messageError[risk.regulationId],
                      [REGULATIONS.COUNTRY_CODE],
                      '',
                    ) && isStepValidated[activeStep]
                  }
                >
                  {countries.map((country: SagisConfig) => (
                    <MenuItem value={country.ItemKey} key={country.ItemKey}>
                      {country.ItemDescription}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                options={getCities(
                  get(listings, 'villes', []),
                  get(risk, 'regulationInfo.countryCode', ''),
                )}
                value={getChosenCity(
                  get(listings, 'villes', []),
                  risk,
                  REGULATIONS.TOWN_CODE,
                )}
                id={REGULATIONS.TOWN_CODE}
                defaultValue={getChosenCity(
                  get(listings, 'villes', []),
                  risk,
                  REGULATIONS.TOWN_CODE,
                )}
                getOptionLabel={(city: SagisConfig) => city.ItemDescription}
                autoSelect={true}
                onChange={handleChange(risk) as any}
                filterOptions={filterOptions}
                renderInput={params => (
                  <TextField
                    {...params}
                    name={REGULATIONS.TOWN_CODE}
                    label={translate({ ...messages.town }, props)}
                    error={
                      get(
                        messageError[risk.regulationId],
                        [REGULATIONS.TOWN_CODE],
                        '',
                      ) && isStepValidated[activeStep]
                    }
                    helperText={messageError[REGULATIONS.TOWN_CODE]}
                  />
                )}
                fullWidth
              />
            </Grid>
          </Grid>
        </SahamAccordion>
      )}
    </StyledDiv>
  );
};

export default injectIntl(AdditionalWarrentiesInformation);
